import React, { useState, useEffect } from 'react';
import {
  House, Package, TextAa, Translate, Image, StackSimple,
  ClockCounterClockwise, Gear,
  Headset, List, X, Rocket, MagnifyingGlass
} from '@phosphor-icons/react';
import { useRuntime } from '../context/RuntimeContext';
import { APP_VERSION } from '../config/appVersion';

// Menu items: `labelKey` maps to i18n, `id` matches activePage
const menuItems = [
  { id: 'home',         labelKey: 'nav.overview',      icon: House },
  { id: 'products',     labelKey: 'nav.catalog',        icon: Package },
  { id: 'content',      labelKey: 'nav.content',        icon: TextAa },
  { id: 'translations', labelKey: 'nav.translations',   icon: Translate },
  { id: 'images',       labelKey: 'nav.images',         icon: Image },
  { id: 'batch',        labelKey: 'nav.batch',          icon: StackSimple },
  { id: 'speed',          labelKey: 'nav.speed',          icon: Rocket },
  { id: 'search-console', labelKey: 'nav.searchConsole',  icon: MagnifyingGlass },
  { id: 'history',       labelKey: 'nav.history',        icon: ClockCounterClockwise },
  { id: 'settings',     labelKey: 'nav.settings',       icon: Gear },
  { id: 'support',      labelKey: 'nav.support',        icon: Headset },
];

const SidebarBase = ({ activePage, onPageChange, sidebarMode = 'expanded' }) => {
  const { t } = useRuntime();

  // sidebarMode: 'expanded' | 'collapsed' | 'hidden'
  // 'collapsed' = icon-only; 'hidden' = completely gone on desktop
  const [collapsed, setCollapsed] = useState(sidebarMode === 'collapsed');
  const [mobileOpen, setMobileOpen] = useState(false);

  // Sync whenever the setting changes from outside
  useEffect(() => {
    setCollapsed(sidebarMode === 'collapsed');
  }, [sidebarMode]);

  const handlePageChange = (pageId) => {
    onPageChange(pageId);
    setMobileOpen(false);
  };

  // 'hidden' hides the entire sidebar on desktop (mobile toggle still works)
  const isHiddenDesktop = sidebarMode === 'hidden';

  return (
    <>
      {/* Mobile toggle */}
      <button
        data-testid="mobile-menu-toggle"
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white border border-gray-200 hover:bg-gray-50"
        onClick={() => setMobileOpen(!mobileOpen)}
      >
        {mobileOpen ? <X size={20} /> : <List size={20} />}
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/20 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`
          sidebar-root fixed lg:static inset-y-0 left-0 z-40
          bg-white border-r border-gray-200
          transition-all duration-300 ease-in-out
          ${collapsed ? 'w-16' : 'w-64'}
          ${isHiddenDesktop ? 'lg:hidden' : ''}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          flex flex-col h-screen
        `}
      >
        {/* Logo */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className={`flex items-center gap-2 ${collapsed ? 'justify-center w-full' : ''}`}>
            <img src="/logo.png" alt="AI Optimizer" className="w-9 h-9 object-contain" />
            {!collapsed && (
              <span className="font-heading font-bold text-sm tracking-tight">AI Optimizer</span>
            )}
          </div>
          <button
            data-testid="sidebar-collapse-toggle"
            className="hidden lg:block p-1 hover:bg-gray-100"
            onClick={() => setCollapsed((c) => !c)}
          >
            <List size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <ul className="space-y-1 px-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = activePage === item.id;
              const label = t(item.labelKey, item.labelKey.split('.').pop());
              return (
                <li key={item.id}>
                  <button
                    data-testid={`nav-${item.id}`}
                    onClick={() => handlePageChange(item.id)}
                    title={collapsed ? label : undefined}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2 text-sm
                      transition-colors cursor-pointer
                      ${isActive
                        ? 'bg-gray-100 text-black font-medium border-l-2 border-black -ml-0.5'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}
                      ${collapsed ? 'justify-center' : ''}
                    `}
                  >
                    <Icon size={20} weight={isActive ? 'fill' : 'regular'} />
                    {!collapsed && <span>{label}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="p-4 border-t border-gray-200">
          {!collapsed && <p className="text-xs text-gray-400">{APP_VERSION}</p>}
        </div>
      </aside>
    </>
  );
};

export const Sidebar = React.memo(SidebarBase);
export default Sidebar;
